/**
 * Neutral init template for loadable .vital JSON.
 * All content is synthetic — no Vital factory presets or samples.
 *
 * synth_version policy: emit "1.0.0" (skips legacy migrations; accepted by
 * modern builds that refuse newer feature versions). Vital GUI version matrix
 * verification is pending dogfood — see fixtures/vital/README.md.
 */
import { PARAM_RANGES } from "@defibrillator/preset-core";
import { bytesToBase64 } from "./base64.js";
import { gainToVitalVolume, hzToVitalCutoff, hzToVitalLfoFrequency, secondsToVitalEnvTime, } from "./conversions.js";
import { PARAM_TO_VITAL } from "./param-map.js";
export { PARAM_TO_VITAL } from "./param-map.js";
export { gainToVitalVolume, hzToVitalCutoff, hzToVitalLfoFrequency, secondsToVitalEnvTime, vitalLfoFrequencyToHz, vitalCutoffToHz, vitalVolumeToGain, vitalEnvTimeToSeconds, VITAL_ENV_TIME_MAX, ConversionError, } from "./conversions.js";
/** Emit compatibility version. Matrix verification pending. */
export const SYNTH_VERSION = "1.0.0";
export const MODULATION_SLOT_COUNT = 64;
export const LFO_COUNT = 8;
export const OSC_COUNT = 3;
const ENV_TIME_PARAMS = new Set([
    "env1_delay",
    "env1_attack",
    "env1_hold",
    "env1_decay",
    "env1_release",
    "env2_delay",
    "env2_attack",
    "env2_hold",
    "env2_decay",
    "env2_release",
    "env3_delay",
    "env3_attack",
    "env3_hold",
    "env3_decay",
    "env3_release",
    "env4_delay",
    "env4_attack",
    "env4_hold",
    "env4_decay",
    "env4_release",
]);
const LFO_FREQ_PARAMS = new Set([
    "lfo1_frequency",
    "lfo2_frequency",
    "lfo3_frequency",
    "lfo4_frequency",
    "lfo5_frequency",
    "lfo6_frequency",
    "lfo7_frequency",
    "lfo8_frequency",
]);
export function isEnvTimeParam(param) {
    return ENV_TIME_PARAMS.has(param);
}
export function isLfoFrequencyParam(param) {
    return LFO_FREQ_PARAMS.has(param);
}
export function mapParamValue(param, value) {
    if (param === "filter_cutoff")
        return hzToVitalCutoff(value);
    if (param === "volume")
        return gainToVitalVolume(value);
    if (isEnvTimeParam(param))
        return secondsToVitalEnvTime(value);
    if (isLfoFrequencyParam(param))
        return hzToVitalLfoFrequency(value);
    if (param === "glide_time") {
        // Vital portamento_time is exponential seconds storage (log2-ish); write seconds via exp encode.
        if (value <= 0)
            return -10;
        return Math.log2(value);
    }
    if (param === "reverb_decay") {
        // reverb_decay_time is exponential seconds
        if (value <= 0)
            return -10;
        return Math.log2(value);
    }
    if (param === "voice_mode") {
        // Handled specially in emit (polyphony + legato). Pass through unused.
        return value;
    }
    if (param === "sub_destination_filter") {
        // Mapped separately; avoid double-writing sample input from this alias.
        return value;
    }
    return value;
}
function encodeInt16Pcm(samples) {
    const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
    return bytesToBase64(bytes);
}
/** Short synthetic silence — not a factory sample. */
export function createSilentSample() {
    const length = 64;
    const pcm = new Int16Array(length);
    return {
        name: "Silence",
        length,
        sample_rate: 44100,
        samples: encodeInt16Pcm(pcm),
    };
}
function emptyModulations() {
    return Array.from({ length: MODULATION_SLOT_COUNT }, () => ({
        source: "",
        destination: "",
    }));
}
/** Built-in LineGenerator shapes used for LFO slots. */
export function lfoShapeByName(shape) {
    switch (shape) {
        case "sine":
            return {
                name: "Sine",
                num_points: 9,
                points: [
                    0, 0.5, 0.125, 0.853553, 0.25, 1, 0.375, 0.853553, 0.5, 0.5, 0.625, 0.146447, 0.75, 0,
                    0.875, 0.146447, 1, 0.5,
                ],
                powers: [0, 0, 0, 0, 0, 0, 0, 0, 0],
                smooth: true,
            };
        case "triangle":
            return {
                name: "Triangle",
                num_points: 3,
                points: [0, 1, 0.5, 0, 1, 1],
                powers: [0, 0, 0],
                smooth: false,
            };
        case "square":
            return {
                name: "Square",
                num_points: 5,
                points: [0, 1, 0.5 - 1e-6, 1, 0.5, 0, 1 - 1e-6, 0, 1, 1],
                powers: [0, 0, 0, 0, 0],
                smooth: false,
            };
        case "saw":
            return {
                name: "Saw",
                num_points: 3,
                points: [0, 1, 1 - 1e-6, 0, 1, 1],
                powers: [0, 0, 0],
                smooth: false,
            };
        case "random":
            return {
                name: "Random",
                num_points: 5,
                points: [0, 0.2, 0.25, 0.8, 0.5, 0.35, 0.75, 0.9, 1, 0.2],
                powers: [0, 0, 0, 0, 0],
                smooth: false,
            };
        case "sample_and_hold":
            return {
                name: "Sample & Hold",
                num_points: 5,
                points: [0, 0.1, 0.25, 0.1, 0.25, 0.7, 0.5, 0.7, 1, 0.3],
                powers: [0, 0, 0, 0, 0],
                smooth: false,
            };
    }
}
export function customLfoShape(name, points, smooth) {
    const ordered = [...points].sort((a, b) => a.x - b.x);
    const flat = [];
    const powers = [];
    for (const pt of ordered) {
        flat.push(pt.x, pt.y);
        powers.push(pt.power ?? 0);
    }
    return {
        name,
        num_points: ordered.length,
        points: flat,
        powers,
        smooth,
    };
}
export function stepsToLfoShape(name, values, smoothing = 0) {
    const n = values.length;
    const points = [];
    for (let i = 0; i < n; i++) {
        const x0 = i / n;
        const y = values[i] ?? 0;
        if (i > 0)
            points.push({ x: x0, y: values[i - 1] ?? 0 });
        points.push({ x: x0, y });
    }
    points.push({ x: 1, y: values[n - 1] ?? 0 });
    for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const cur = points[i];
        if (cur.x <= prev.x)
            cur.x = Math.min(1, prev.x + 1e-6);
    }
    points[0].x = 0;
    points[points.length - 1].x = 1;
    return customLfoShape(name, points, smoothing > 0.5);
}
function defaultLfos() {
    const shapes = [
        "sine",
        "triangle",
        "square",
        "saw",
        "sine",
        "sine",
        "sine",
        "sine",
    ];
    return shapes.map((s) => lfoShapeByName(s));
}
function mvpDefaults() {
    const out = {
        osc_1_on: 1,
        osc_2_on: 0,
        osc_3_on: 0,
        filter_1_mix: 1,
        filter_1_osc1_input: 1,
        filter_1_osc2_input: 0,
        filter_1_osc3_input: 0,
        polyphony: 8,
        voice_priority: 0,
        legato: 0,
        // LFO sync defaults: free Hz mode (Seconds = 0? Vital default sync=1 Tempo)
        // Application free_hz → sync option for "Seconds"/time. Indexed sync: 0=time typically.
    };
    for (const [param, vitalKey] of Object.entries(PARAM_TO_VITAL)) {
        if (param === "voice_mode" || param === "sub_destination_filter")
            continue;
        if (param === "osc1_destination_filter" ||
            param === "osc2_destination_filter" ||
            param === "osc3_destination_filter") {
            // Written via filter_*_input keys; skip duplicate default write from destination aliases.
            continue;
        }
        const range = PARAM_RANGES[param];
        out[vitalKey] = mapParamValue(param, range.default);
    }
    for (let i = 1; i <= LFO_COUNT; i++) {
        out[`lfo_${i}_sync`] = 0; // time / free Hz
        out[`lfo_${i}_sync_type`] = 0; // note retrigger
        out[`lfo_${i}_tempo`] = 7;
        out[`lfo_${i}_phase`] = 0;
        out[`lfo_${i}_fade_time`] = 0;
        out[`lfo_${i}_delay_time`] = 0;
        out[`lfo_${i}_stereo`] = 0;
    }
    for (let i = 1; i <= MODULATION_SLOT_COUNT; i++) {
        out[`modulation_${i}_amount`] = 0;
        out[`modulation_${i}_power`] = 0;
        out[`modulation_${i}_bipolar`] = 0;
        out[`modulation_${i}_stereo`] = 0;
        out[`modulation_${i}_bypass`] = 0;
    }
    return out;
}
/**
 * Deep-cloneable neutral init document.
 * Wavetables are placeholders; compileVital replaces them from generators.
 */
export function createInitVitalJson() {
    const scalars = mvpDefaults();
    const settings = {
        ...scalars,
        modulations: emptyModulations(),
        lfos: defaultLfos(),
        wavetables: [],
        sample: createSilentSample(),
    };
    return {
        synth_version: SYNTH_VERSION,
        preset_name: "Init",
        author: "Defibrillator",
        comments: "",
        preset_style: "Template",
        macro1: "MACRO 1",
        macro2: "MACRO 2",
        macro3: "MACRO 3",
        macro4: "MACRO 4",
        settings,
    };
}
//# sourceMappingURL=init-template.js.map